import { Link, useLocation } from 'react-router-dom';

import { Button, Group, Menu } from '@mantine/core';
import {
  IconActivity,
  IconAdjustments,
  IconBook,
  IconBriefcase,
  IconBuildingStore,
  IconChartBar,
  IconChevronDown,
  IconChevronRight,
  IconCoin,
  IconEyeSearch,
  IconFileCertificate,
  IconGauge,
  IconLayoutDashboard,
  IconListCheck,
  IconListDetails,
  IconPuzzle,
  IconRadar,
  IconReceipt,
  IconSettings,
  IconShieldCog,
  IconTarget,
  IconUsersGroup,
} from '@tabler/icons-react';

import type { RegistryAttention } from '@/types/registry-documents';
import { navCountBadge } from './navCountBadge';
import classes from './TopNav.module.css';

const ADMIN_PREFIXES = [
  '/workbench',
  '/admin/report-configs',
  '/admin/scope-components',
  '/admin/network-partners',
  '/admin/client-settings',
  '/admin/monitoring',
  '/admin/registry-documents',
  '/admin/screening-batches',
  '/admin/risk-profiles',
  '/admin/playbooks',
  '/admin/provider-rates',
  '/admin/usage',
  '/admin/cogs',
];

interface AdminMenuProps {
  /** Open registry-document inbox items (see `useRegistryAttention`); null
   * until the first read. */
  registryAttention?: RegistryAttention | null;
}

/** What the registry-documents count is made of, as the badge's hover text. */
function describeRegistryAttention(attention: RegistryAttention): string {
  // The counts are shared between tabs, and a tab still on an earlier build
  // shares them without the replies or the unmatched mail.
  const replies = attention.replies ?? 0;
  const unmatched = attention.unmatched ?? 0;
  return `Registry documents: ${attention.documentsReceived} received, ${attention.overdue} overdue, ${replies} replied, ${unmatched} unmatched mail`;
}

interface GroupProps {
  label: string;
  icon: React.ReactNode;
  /** What its entries carry (a count), repeated on the group so it is seen
   * without opening it. */
  badge?: React.ReactNode;
  children: React.ReactNode;
}

/** A group of the menu's pages, opened on hover: the pages it holds. */
function AdminGroup({ label, icon, badge, children }: Readonly<GroupProps>) {
  return (
    <Menu.Sub>
      <Menu.Sub.Target>
        <Menu.Sub.Item
          leftSection={icon}
          rightSection={
            badge ? (
              <Group gap={4} wrap="nowrap">
                {badge}
                <IconChevronRight size={14} />
              </Group>
            ) : undefined
          }
        >
          {label}
        </Menu.Sub.Item>
      </Menu.Sub.Target>
      <Menu.Sub.Dropdown>{children}</Menu.Sub.Dropdown>
    </Menu.Sub>
  );
}

/**
 * The admin pages, grouped by what they hold: the Workbench (its cases and
 * the playbooks), configuration (clients, report configs, scope components,
 * risk profiles), fulfilment (partners, registry documents, screening
 * batches), monitoring, and billing.
 */
export function AdminMenu({ registryAttention = null }: Readonly<AdminMenuProps>) {
  const location = useLocation();
  const active = ADMIN_PREFIXES.some((p) => location.pathname.startsWith(p));
  const registryCount = registryAttention?.total ?? 0;
  const registryTitle = registryAttention
    ? describeRegistryAttention(registryAttention)
    : undefined;

  return (
    <Menu position="bottom-start" trigger="click-hover" openDelay={50} closeDelay={150}>
      <Menu.Target>
        <Button
          variant="subtle"
          className={`${classes.navButton} ${active ? classes.navButtonActive : ''}`}
          leftSection={<IconShieldCog size={18} />}
          // The drop-down is closed most of the time, so the count its
          // entries carry is repeated on the button.
          rightSection={
            <Group gap={4} wrap="nowrap">
              {navCountBadge(registryCount, 'admin-nav-badge', registryTitle)}
              <IconChevronDown size={14} />
            </Group>
          }
          styles={{
            root: { color: 'var(--mantine-color-white)' },
            label: { color: 'var(--mantine-color-white)' },
            section: { color: 'var(--mantine-color-white)' },
          }}
        >
          Admin
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <AdminGroup label="Workbench" icon={<IconLayoutDashboard size={16} />}>
          <Menu.Item component={Link} to="/workbench" leftSection={<IconListDetails size={16} />}>
            Cases
          </Menu.Item>
          <Menu.Item component={Link} to="/admin/playbooks" leftSection={<IconBook size={16} />}>
            Playbooks
          </Menu.Item>
        </AdminGroup>
        <AdminGroup label="Configuration" icon={<IconSettings size={16} />}>
          <Menu.Item
            component={Link}
            to="/admin/client-settings"
            leftSection={<IconBuildingStore size={16} />}
          >
            Client Settings
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/admin/report-configs"
            leftSection={<IconAdjustments size={16} />}
          >
            Report Configs
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/admin/scope-components"
            leftSection={<IconPuzzle size={16} />}
          >
            Scope Components
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/admin/risk-profiles"
            leftSection={<IconTarget size={16} />}
          >
            Risk Profiles
          </Menu.Item>
        </AdminGroup>
        <AdminGroup
          label="Fulfilment"
          icon={<IconBriefcase size={16} />}
          badge={navCountBadge(registryCount, 'admin-menu-fulfilment-badge', registryTitle)}
        >
          <Menu.Item
            component={Link}
            to="/admin/network-partners"
            leftSection={<IconUsersGroup size={16} />}
          >
            Network Partners
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/admin/registry-documents"
            leftSection={<IconFileCertificate size={16} />}
            rightSection={navCountBadge(registryCount, 'admin-menu-registry-badge', registryTitle)}
          >
            Registry Documents
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/admin/screening-batches"
            leftSection={<IconListCheck size={16} />}
          >
            Screening Batches
          </Menu.Item>
        </AdminGroup>
        <AdminGroup label="Monitoring" icon={<IconActivity size={16} />}>
          <Menu.Item
            component={Link}
            to="/admin/monitoring/alerts"
            leftSection={<IconEyeSearch size={16} />}
          >
            Monitoring Alerts
          </Menu.Item>
          <Menu.Item
            component={Link}
            to="/admin/monitoring/subjects"
            leftSection={<IconRadar size={16} />}
          >
            Monitored Subjects
          </Menu.Item>
        </AdminGroup>
        <AdminGroup label="Billing" icon={<IconReceipt size={16} />}>
          <Menu.Item
            component={Link}
            to="/admin/provider-rates"
            leftSection={<IconCoin size={16} />}
          >
            Provider Rates
          </Menu.Item>
          <Menu.Item component={Link} to="/admin/usage" leftSection={<IconGauge size={16} />}>
            Usage
          </Menu.Item>
          <Menu.Item component={Link} to="/admin/cogs" leftSection={<IconChartBar size={16} />}>
            Cost of Work
          </Menu.Item>
        </AdminGroup>
      </Menu.Dropdown>
    </Menu>
  );
}
